/**
 * Adapters: maps backend API types → frontend UI types.
 * Keeps UI components decoupled from the backend field names.
 */

import type { ApiStaff, ApiJob, ApiAssignment, ApiBoardPostponement } from './api';
import type { Staff, Job, StaffRole } from '../app/data/mockData';

function toStaffRole(s: ApiStaff): StaffRole {
  if (s.is_subcontractor) return 'subcontractor';
  return s.staff_role; // 'operative' | 'manager' | 'back_office' — set by admin, returned by API
}

/**
 * Escapes regex metacharacters in a string so it can safely be embedded in
 * `new RegExp(...)`. Module-private — used only by extractDealName.
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Derives a human-readable display name from the raw HubSpot deal name.
 *
 * Input format: "<ION> - <DealName> (<CompanyName>)"
 * e.g. "15620 - Tom Walker & Sons Ltd - New Deal (Tom Walker & Sons Ltd)"
 * Output: "Tom Walker & Sons Ltd - New Deal"
 *
 * Strips the ION prefix and, if client is provided, the trailing company
 * parenthetical. Falls back to rawName unchanged if parsing produces empty.
 */
export function extractDealName(
  rawName: string,
  ion: string | null | undefined,
  client?: string | null,
): string {
  if (!ion) return rawName;

  // Strip ION prefix: "15620 - ..."
  const ionPattern = new RegExp('^' + escapeRegExp(ion) + '\\s*-+\\s*');
  if (!ionPattern.test(rawName)) return rawName;
  let remainder = rawName.replace(ionPattern, '').trim();
  if (remainder === '') return rawName;

  // Strip company suffix: "... (Tom Walker & Sons Ltd)"
  if (client) {
    const suffixPattern = new RegExp('\\s*\\(' + escapeRegExp(client) + '\\)\\s*$');
    remainder = remainder.replace(suffixPattern, '').trim();
  }

  return remainder || rawName;
}

export function adaptStaff(s: ApiStaff, consecutiveDays?: number): Staff {
  return {
    id: s.id,
    name: s.name,
    initials: s.initials,
    // Phase 26-05: personal email surfaced from ApiStaff.email (Xero source of truth).
    personalEmail: s.email ?? null,
    knownAs: s.known_as ?? undefined,
    workEmail: s.work_email ?? null,
    role: toStaffRole(s),
    operationalRole: null,
    isFieldOperative: true,
    skills: [],
    leaveStatus: 'none',
    // GH #173: consecutive-days streak from GET /staff/consecutive-days, merged
    // in by useStaff. Operatives-only server-side; non-operatives stay undefined
    // (the staff card then renders "-").
    consecutiveDays,
    maxHoursPerDay: Number(s.default_hours),
    // #234 Task 7: labour cost £/hr. pg serialises NUMERIC as a string; coerce to
    // a number for the edit form, preserving null (blended-rate fallback).
    costRate: s.cost_rate != null ? Number(s.cost_rate) : null,
    companyName: s.company_name ?? undefined,
    xeroEmployeeId: s.xero_employee_id,
    // 20-03: pass through admin-control fields for Staff & Subs Staff tab.
    authRole: s.auth_role,
    canClockIn: s.can_clock_in,
    isActive: s.is_active,
  };
}

export function adaptJob(j: ApiJob, assignments: ApiAssignment[] = []): Job {
  const jobAssignments = assignments.filter((a) => a.job_id === j.id);

  return {
    id: j.id,
    // Phase 78-01: carry the work_item UUID so the panel's allocation-driven
    // Materials section can query api.materials.workItemAllocations(workItemId).
    workItemId: j.work_item_id,
    ion: j.ion,
    dealName: j.name,
    displayName: extractDealName(j.name, j.ion, j.client_name),
    date: new Date(j.start_date),
    startDate: j.start_date,
    durationDays: j.end_date
      ? Math.ceil(
          (new Date(j.end_date).getTime() - new Date(j.start_date).getTime()) /
            (1000 * 60 * 60 * 24),
        ) + 1
      : 1,
    status: j.status,
    assignedStaff: jobAssignments.map((a) => ({
      assignmentId: a.id,
      staffId: a.staff_id,
      hours:
        (new Date(`1970-01-01T${a.end_time}`).getTime() -
          new Date(`1970-01-01T${a.start_time}`).getTime()) /
        3600000,
      startTime: a.start_time,
      endTime: a.end_time,
      date: a.date,
      isTeamLeader: a.is_team_leader ?? false,
      // Phase 39-07 (resource-scoping merge): project phase_id so the panel
      // can filter assignedStaff by the active phase. Optional because
      // pre-39-06 assignments may pre-date the phase_id column population.
      phaseId: a.phase_id ?? undefined,
    })),
    details: j.notes ?? '',
    dailyScopes: (j.daily_scopes as Record<string, string>) ?? {},
    equipment: [],
    materials: [],
    warnings: [],
    client: j.client_name,
    companyDomain: j.company_domain ?? null,
    location: j.site_address ?? undefined,
    locationVerified: j.site_address_verified ?? false,
    driveFolderId: j.drive_folder_id ?? null,
    contractsManagerId: j.contracts_manager_id ?? null,
    // 80-02: the single phase-status survivor (was the now-deleted booking-status field).
    // Non-board /jobs/:id callers get the Phase-1 projection's phase_status.
    phaseStatus: j.phase_status,
    source: j.source,
    sourcePortalId: j.source_portal_id ?? null,
    // Phase 32-02 (Issue #34 sub-item 1): pass-through linked-deal metadata.
    linkedDealId: j.linked_deal_id ?? null,
    linkedDealIon: j.linked_deal_ion ?? null,
    linkedDealName: j.linked_deal_name ?? null,
    linkedDealClient: j.linked_deal_client ?? null,
    // Phase 32-05: deal's own site_address — distinct from j.site_address
    // (this ticket's resolved worksite). Sourced by the LinkedDealSection popover.
    linkedDealSiteAddress: j.linked_deal_site_address ?? null,
    // Phase 32-04 (Issue #34 sub-item 2): raw HubSpot ticket.location enum.
    locationSource: j.location_source ?? null,
    // Per-phase profitability: linked HubSpot deal amount (job-level Amount).
    hubspotAmount: j.hubspot_amount ?? null,
  };
}

/**
 * Phase 39-05: adapt a board phase-row (one element of `boardData.jobs[]` from
 * the per-(phase, date) projection in 39-04) into a `Job` for the board's
 * layout pipeline. Critical difference from `adaptJob`: this sets `id =
 * phase_id` so `assignJobRows` treats each phase as a distinct layout entity
 * (multi-phase jobs get distinct row slots; phases of the same parent job
 * render as separate cards). `parentJobId` carries the actual job UUID for
 * cross-references (vehicleRegsByJobId for the FE filter still operates per-
 * job; JobDetailPanel open via separate `useJobs()` lookup).
 */
/**
 * Phase 39-05 follow-up (2026-05-25, Migration 065): adapt a self-contained
 * postponement snapshot into a synthetic Job for the board's layout
 * pipeline. The resulting Job:
 *
 *   - has `id = postponement.id` (unique per snapshot; FE dedup + React
 *     key handled naturally — multi-phase + multi-postpone all distinct);
 *   - has `status = 'postponed'` (forced regardless of the parent job's
 *     current status — even if the job has since been rescheduled, the
 *     historic tombstone keeps rendering as "POSTPONED");
 *   - has `parentJobId = original_job_id` so the panel click handler
 *     can route to the live parent (if it still exists);
 *   - has phaseNumber from the snapshot, so the JobCard tombstone could
 *     surface "Phase N" context (currently the parent-status tombstone
 *     branch doesn't render phase number — could be added if useful).
 */
export function adaptPostponement(p: ApiBoardPostponement): Job {
  const start = new Date(p.start_date);
  const end = new Date(p.end_date);
  const durationDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  return {
    id: p.id,
    parentJobId: p.original_job_id,
    phaseId: undefined,
    phaseNumber: p.phase_number,
    phaseStatus: undefined,
    phaseCountTotal: undefined,
    cancelledAt: null,
    cancelledReason: null,
    ion: p.ion,
    dealName: p.name,
    displayName: extractDealName(p.name, p.ion, p.client_name),
    date: start,
    startDate: p.start_date,
    durationDays,
    status: 'postponed',
    assignedStaff: [],
    details: '',
    dailyScopes: {},
    equipment: [],
    materials: [],
    warnings: [],
    client: p.client_name ?? undefined,
    companyDomain: null,
    location: undefined,
    locationVerified: false,
    driveFolderId: null,
    contractsManagerId: null,
    source: p.source ?? 'manual',
    linkedDealId: null,
    linkedDealIon: null,
    linkedDealName: null,
    linkedDealClient: null,
    linkedDealSiteAddress: null,
    locationSource: null,
  };
}

export function adaptBoardPhaseRow(j: ApiJob, assignments: ApiAssignment[] = []): Job {
  if (!j.phase_id) {
    // Defensive — board projection always includes phase_id post-39-04. If
    // missing, the board endpoint contract is broken; fall back to the legacy
    // adapter so single-card-per-job legacy behaviour is preserved.
    return adaptJob(j, assignments);
  }
  // Phase 40-01 fixup (Tom UAT 2026-05-26): filter assignments to THIS PHASE
  // only so a phase card's assignedStaff doesn't bleed in staff from its
  // sibling phases. assignment_segments.phase_id has been NOT NULL since
  // Migration 061 + ApiAssignment.phase_id was projected onto the board
  // segment shape in 39-07. Legacy null fall-through stays for paranoia.
  const phaseAssignments = assignments.filter(
    (a) => a.phase_id === j.phase_id || a.phase_id == null,
  );
  // Reuse the core adapter for shared fields, then overlay phase context +
  // override `id` to phase_id for layout-uniqueness.
  const base = adaptJob(j, phaseAssignments);

  // Phase 39-07 follow-up: each board row represents one PHASE — its dates,
  // CM, status, notes, and scopes come from job_phases, NOT jobs.
  // (The board SELECT previously had `j.*, jp.contracts_manager_id, ...`
  // which silently kept the jobs-level value due to column-name collision;
  // the BE now aliases jp.* columns with phase_ prefixes so we can overlay
  // them here unambiguously.)
  const phaseStart = j.phase_start_date ?? j.start_date;
  const phaseEnd = j.phase_end_date ?? j.end_date;
  const phaseDurationDays = phaseEnd
    ? Math.ceil(
        (new Date(phaseEnd).getTime() - new Date(phaseStart).getTime()) / (1000 * 60 * 60 * 24),
      ) + 1
    : 1;

  return {
    ...base,
    id: j.phase_id,
    parentJobId: j.id,
    phaseId: j.phase_id,
    phaseNumber: j.phase_number,
    phaseStatus: j.phase_status,
    cancelledAt: j.cancelled_at ?? null,
    cancelledReason: j.cancelled_reason ?? null,
    // Phase 39-05 Spec [2] amendment (2026-05-22): pass-through
    // phase_count_total so the JobCard badge denominator is stable
    // across the whole parent job, not just the loaded window.
    phaseCountTotal: j.phase_count_total,
    // Phase 39-07 follow-up: per-phase overlays.
    startDate: phaseStart,
    date: new Date(phaseStart),
    durationDays: phaseDurationDays,
    contractsManagerId: j.phase_contracts_manager_id ?? null,
    details: j.phase_notes ?? '',
    dailyScopes: (j.phase_daily_scopes as Record<string, string>) ?? {},
  };
}
